import { useMemo, useState } from 'react';
import {
  AlarmClock,
  BadgeDollarSign,
  CalendarRange,
  ChevronRight,
  CircleAlert,
  FileSpreadsheet,
  FileText,
  HardHat,
  Layers3,
  Search,
  ShieldCheck,
  Wrench,
} from 'lucide-react';

type ContractStage = 'active' | 'expiring' | 'expired' | 'draft';
type ContractScope = 'İzleme + Bakım' | 'İzleme' | 'Bakım';
type Currency = 'USD' | 'TRY';

type ContractRecord = {
  id: string;
  customer: string;
  plant: string;
  city: string;
  scope: ContractScope;
  stage: ContractStage;
  startDate: string;
  endDate: string;
  amount: number;
  currency: Currency;
  installedPower: string;
  owner: string;
  note: string;
};

type MaintenanceVisit = {
  id: string;
  plant: string;
  customer: string;
  date: string;
  team: string;
  status: 'Planlandı' | 'Sahada' | 'Rapor Bekliyor';
  focus: string;
};

type DocumentRecord = {
  id: string;
  title: string;
  kind: 'Sözleşme' | 'Proforma' | 'Bakım Raporu';
  customer: string;
  updatedAt: string;
  status: 'Hazır' | 'Taslak' | 'Onay Bekliyor';
};

const contractData: ContractRecord[] = [
  {
    id: 'CTR-2026-014',
    customer: 'Bahçıvanlar Basım San. A.Ş.',
    plant: 'Bahçıvanlar GES',
    city: 'Konya / Selçuklu',
    scope: 'İzleme + Bakım',
    stage: 'expiring',
    startDate: '2025-06-01',
    endDate: '2026-06-01',
    amount: 1584,
    currency: 'USD',
    installedPower: '248,52 kWp / 240 kWe',
    owner: 'Merve Kaya',
    note: 'Yıllık yenileme teklifi hazırlanmalı.',
  },
  {
    id: 'CTR-2026-019',
    customer: 'Kentpar Otomotiv Yedek Parça A.Ş.',
    plant: 'Ergun Safir GES',
    city: 'Konya / Karatay',
    scope: 'İzleme + Bakım',
    stage: 'expiring',
    startDate: '2025-05-31',
    endDate: '2026-05-31',
    amount: 240000,
    currency: 'TRY',
    installedPower: '2523,4 kWp / 1920 kWe',
    owner: 'Büşra Çelik',
    note: 'TL kontrat, fiyat güncelleme senaryosu istenecek.',
  },
  {
    id: 'CTR-2026-006',
    customer: 'Uysal Tohum',
    plant: 'Uysal Tohum GES',
    city: 'Konya / Çumra',
    scope: 'İzleme + Bakım',
    stage: 'active',
    startDate: '2026-01-01',
    endDate: '2027-01-01',
    amount: 1750,
    currency: 'USD',
    installedPower: '860 kWp / 720 kWe',
    owner: 'Emre Duran',
    note: 'Saha bakım raporu sözleşme ekine bağlanacak.',
  },
  {
    id: 'CTR-2026-003',
    customer: 'Polipen A.Ş.',
    plant: 'Polipen GES',
    city: 'Konya / Ereğli',
    scope: 'İzleme + Bakım',
    stage: 'expired',
    startDate: '2025-01-23',
    endDate: '2026-01-23',
    amount: 1000,
    currency: 'USD',
    installedPower: '410 kWp / 300 kWe',
    owner: 'Merve Kaya',
    note: 'Yenileme kararı bekleniyor, kırmızı liste.',
  },
  {
    id: 'CTR-2026-021',
    customer: 'Optimas Elk. Müh. Ltd. Şti.',
    plant: 'Optimas Servis Havuzu',
    city: 'Konya / Meram',
    scope: 'Bakım',
    stage: 'draft',
    startDate: '2026-05-15',
    endDate: '2027-05-15',
    amount: 1200,
    currency: 'USD',
    installedPower: 'Portföy bazlı',
    owner: 'Alper Yıldız',
    note: 'Taslak, kapsam maddeleri netleşmedi.',
  },
];

const visitData: MaintenanceVisit[] = [
  {
    id: 'VIS-041',
    plant: 'Bahçıvanlar GES',
    customer: 'Bahçıvanlar Basım',
    date: '2026-04-14 09:30',
    team: 'Ekip Atlas',
    status: 'Planlandı',
    focus: 'İnverter fan kontrolü ve termal kamera turu',
  },
  {
    id: 'VIS-042',
    plant: 'Ergun Safir GES',
    customer: 'Kentpar Otomotiv',
    date: '2026-04-15 11:00',
    team: 'Ekip Vega',
    status: 'Sahada',
    focus: 'SCADA alarm doğrulama ve string kontrolü',
  },
  {
    id: 'VIS-043',
    plant: 'Uysal Tohum GES',
    customer: 'Uysal Tohum',
    date: '2026-04-18 13:30',
    team: 'Ekip Luna',
    status: 'Rapor Bekliyor',
    focus: 'Bakım checklist kapanışı ve fotoğraf teslimi',
  },
];

const documentData: DocumentRecord[] = [
  {
    id: 'DOC-88',
    title: 'Bahçıvanlar 2026 Yenileme Teklifi',
    kind: 'Proforma',
    customer: 'Bahçıvanlar Basım',
    updatedAt: '12 Nisan 2026',
    status: 'Onay Bekliyor',
  },
  {
    id: 'DOC-91',
    title: 'Kentpar İzleme + Bakım Sözleşmesi',
    kind: 'Sözleşme',
    customer: 'Kentpar Otomotiv',
    updatedAt: '10 Nisan 2026',
    status: 'Hazır',
  },
  {
    id: 'DOC-94',
    title: 'Uysal Tohum Nisan Bakım Raporu',
    kind: 'Bakım Raporu',
    customer: 'Uysal Tohum',
    updatedAt: '09 Nisan 2026',
    status: 'Taslak',
  },
];

const stageTone: Record<ContractStage, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expiring: 'bg-amber-50 text-amber-700 border-amber-200',
  expired: 'bg-rose-50 text-rose-700 border-rose-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
};

const stageLabel: Record<ContractStage, string> = {
  active: 'Aktif',
  expiring: 'Yaklaşıyor',
  expired: 'Süresi Doldu',
  draft: 'Taslak',
};

const visitTone: Record<MaintenanceVisit['status'], string> = {
  Planlandı: 'bg-sky-50 text-sky-700 border-sky-200',
  Sahada: 'bg-violet-50 text-violet-700 border-violet-200',
  'Rapor Bekliyor': 'bg-orange-50 text-orange-700 border-orange-200',
};

const docTone: Record<DocumentRecord['status'], string> = {
  Hazır: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Taslak: 'bg-slate-100 text-slate-600 border-slate-200',
  'Onay Bekliyor': 'bg-amber-50 text-amber-700 border-amber-200',
};

function formatMoney(amount: number, currency: Currency) {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function MaintenanceStudioPage() {
  const [search, setSearch] = useState('');
  const [activeStage, setActiveStage] = useState<'all' | ContractStage>('all');

  const filteredContracts = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return contractData.filter((item) => {
      const stageOk = activeStage === 'all' || item.stage === activeStage;
      const queryOk =
        !q ||
        item.customer.toLocaleLowerCase('tr-TR').includes(q) ||
        item.plant.toLocaleLowerCase('tr-TR').includes(q) ||
        item.city.toLocaleLowerCase('tr-TR').includes(q);
      return stageOk && queryOk;
    });
  }, [activeStage, search]);

  const summary = useMemo(() => {
    const expiring = contractData.filter((item) => item.stage === 'expiring').length;
    const expired = contractData.filter((item) => item.stage === 'expired').length;
    const active = contractData.filter((item) => item.stage === 'active').length;
    const annual = contractData.reduce((total, item) => total + item.amount, 0);

    return { expiring, expired, active, annual };
  }, []);

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-1 pb-6 pt-2">
      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#17335c_38%,#245a93_100%)] text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <div className="grid gap-8 px-6 py-7 md:grid-cols-[1.3fr_0.7fr] md:px-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-sky-100">
              <Layers3 className="h-4 w-4" />
              Bakım Stüdyosu
            </div>
            <h1 className="mt-4 max-w-2xl text-3xl font-black tracking-tight md:text-4xl">
              Sözleşme, bakım planı ve doküman akışını tek çalışma yüzeyinde toparlayan yeni bir ekran.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200">
              Bu sayfa mevcut modüle dokunmadan oluşturulmuş konsept bir alan. Veriler placeholder, amaç görsel yön ve bilgi hiyerarşisini birlikte görmek.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">Portföy Tutarı</div>
                <div className="mt-1 text-2xl font-black">{formatMoney(summary.annual, 'TRY')}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">Aktif Kontrat</div>
                <div className="mt-1 text-2xl font-black">{summary.active}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">Acil Takip</div>
                <div className="mt-1 text-2xl font-black">{summary.expiring + summary.expired}</div>
              </div>
            </div>
          </div>

          <div className="grid gap-3">
            <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-rose-400/20 p-3 text-rose-100">
                  <AlarmClock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold">Yaklaşan Yenilemeler</div>
                  <div className="text-xs text-slate-300">Öncelik verilmesi gereken kontratlar</div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-black/10 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Yaklaşıyor</div>
                  <div className="mt-1 text-2xl font-black">{summary.expiring}</div>
                </div>
                <div className="rounded-2xl bg-black/10 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Süresi Doldu</div>
                  <div className="mt-1 text-2xl font-black">{summary.expired}</div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold">Konsept Notu</div>
                  <div className="text-xs text-slate-300">Tablo yerine karar vermeyi hızlandıran kart yapısı</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
              <ul className="mt-4 space-y-2 text-sm text-slate-100">
                <li className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-200" />
                  Sözleşme riski, saha planı ve doküman üretimi aynı yüzeyde.
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-200" />
                  Bakım ekibinin iş yükü ve ticari takip birlikte okunuyor.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={<FileText className="h-5 w-5" />}
          tone="text-sky-700 bg-sky-50 border-sky-200"
          label="Toplam Sözleşme"
          value={String(contractData.length)}
          hint="Placeholder portföy"
        />
        <MetricCard
          icon={<Wrench className="h-5 w-5" />}
          tone="text-violet-700 bg-violet-50 border-violet-200"
          label="Planlı Bakım"
          value={String(visitData.length)}
          hint="Bu hafta ajandada"
        />
        <MetricCard
          icon={<BadgeDollarSign className="h-5 w-5" />}
          tone="text-amber-700 bg-amber-50 border-amber-200"
          label="USD Ağırlıklı Kontrat"
          value="3"
          hint="Kur hassasiyeti olan dosyalar"
        />
        <MetricCard
          icon={<CircleAlert className="h-5 w-5" />}
          tone="text-rose-700 bg-rose-50 border-rose-200"
          label="Aksiyon Gerekli"
          value={String(summary.expiring + summary.expired)}
          hint="Yenileme veya karar bekliyor"
        />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.55fr_0.95fr]">
        <div className="space-y-5">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Sözleşme Yönetimi</div>
                <h2 className="mt-1 text-xl font-black tracking-tight text-slate-900">Portföy Görünümü</h2>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Müşteri, santral veya lokasyon ara"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-400 focus:bg-white md:w-72"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'all', label: 'Tümü' },
                    { id: 'active', label: 'Aktif' },
                    { id: 'expiring', label: 'Yaklaşan' },
                    { id: 'expired', label: 'Süresi Dolan' },
                    { id: 'draft', label: 'Taslak' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveStage(item.id as 'all' | ContractStage)}
                      className={`rounded-full border px-3 py-2 text-xs font-bold transition ${
                        activeStage === item.id
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-900'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {filteredContracts.map((item) => (
                <article
                  key={item.id}
                  className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4 transition hover:border-slate-300 hover:bg-white"
                >
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${stageTone[item.stage]}`}>
                          {stageLabel[item.stage]}
                        </span>
                        <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white">
                          {item.scope}
                        </span>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{item.id}</span>
                      </div>
                      <h3 className="mt-3 text-lg font-black tracking-tight text-slate-900">{item.customer}</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {item.plant} • {item.city}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-900 px-4 py-3 text-white">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Yıllık Tutar</div>
                      <div className="mt-1 text-xl font-black">{formatMoney(item.amount, item.currency)}</div>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <InfoBlock label="Başlangıç" value={item.startDate} />
                    <InfoBlock label="Bitiş" value={item.endDate} />
                    <InfoBlock label="Kurulu Güç" value={item.installedPower} />
                    <InfoBlock label="Sorumlu" value={item.owner} />
                  </div>

                  <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                    {item.note}
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Tablo Görünümü</div>
                <h2 className="mt-1 text-xl font-black tracking-tight text-slate-900">Sözleşme Portföy Tablosu</h2>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500">
                {filteredContracts.length} kayıt
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-left">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    <th className="px-4 py-3 font-bold">Müşteri</th>
                    <th className="px-4 py-3 font-bold">Santral</th>
                    <th className="px-4 py-3 font-bold">Kapsam</th>
                    <th className="px-4 py-3 font-bold">Tutar</th>
                    <th className="px-4 py-3 font-bold">Başlangıç</th>
                    <th className="px-4 py-3 font-bold">Bitiş</th>
                    <th className="px-4 py-3 font-bold">Durum</th>
                    <th className="px-4 py-3 font-bold">Sorumlu</th>
                    <th className="px-4 py-3 font-bold">Not</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map((item, index) => (
                    <tr
                      key={`${item.id}-table`}
                      className={`border-b border-slate-100 text-sm text-slate-700 transition hover:bg-slate-50 ${
                        item.stage === 'expired'
                          ? 'bg-rose-50/60'
                          : item.stage === 'expiring'
                            ? 'bg-amber-50/50'
                            : index % 2 === 0
                              ? 'bg-white'
                              : 'bg-slate-50/40'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900">{item.customer}</div>
                        <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{item.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{item.plant}</div>
                        <div className="mt-1 text-xs text-slate-500">{item.city}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600">
                          {item.scope}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-black text-slate-900">{formatMoney(item.amount, item.currency)}</div>
                        <div className="mt-1 text-xs text-slate-500">{item.currency}</div>
                      </td>
                      <td className="px-4 py-3 font-medium">{item.startDate}</td>
                      <td className="px-4 py-3 font-medium">{item.endDate}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${stageTone[item.stage]}`}>
                          {stageLabel[item.stage]}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.owner}</td>
                      <td className="max-w-[260px] px-4 py-3 text-xs text-slate-500">
                        <span className="block truncate" title={item.note}>
                          {item.note}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Yeni Sözleşme</div>
              <h2 className="mt-1 text-xl font-black tracking-tight text-slate-900">Sözleşme Oluşturma Ekranı Taslağı</h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-500">
                Bu alan gerçek backend akışına bağlı değil. Amaç, yeni sözleşme girişinin daha düzenli, daha satış-operasyon ortak kullanımına uygun bir form dilini denemek.
              </p>
            </div>

            <div className="grid gap-6 px-5 py-5 xl:grid-cols-[1.15fr_0.85fr]">
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField label="Sözleşme Başlığı" value="2026 İzleme + Bakım Yenileme Paketi" />
                  <FormField label="Referans Kodu" value="CNT-2026-NEW-08" />
                  <FormField label="Yatırımcı / Firma" value="Bahçıvanlar Basım San. A.Ş." />
                  <FormField label="Sözleşme Sahibi" value="Merve Kaya" />
                  <FormField label="E-posta" value="destek@bahcivanlar.com" />
                  <FormField label="Telefon" value="+90 532 000 00 12" />
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-black text-slate-900">Sözleşme Yapısı</div>
                      <div className="text-xs text-slate-500">Ticari çerçeve ve kapsam seçimleri</div>
                    </div>
                    <span className="rounded-full bg-sky-50 px-3 py-1 text-[11px] font-bold text-sky-700">Taslak Senaryo</span>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <FormField label="Sözleşme Türü" value="İzleme + Bakım" />
                    <FormField label="Para Birimi" value="USD" />
                    <FormField label="Başlangıç Tarihi" value="01.06.2026" />
                    <FormField label="Süre" value="1 Yıl" />
                    <FormField label="Yıllık Tutar" value="$ 1.584" />
                    <FormField label="Yenileme Kuralı" value="30 gün önce teklif oluştur" />
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4">
                  <div className="text-sm font-black text-slate-900">Santral Satırları</div>
                  <div className="mt-1 text-xs text-slate-500">Bir sözleşme içinde birden fazla santral ve farklı ücret kalemi toplanabilir.</div>

                  <div className="mt-4 space-y-3">
                    <LineCard
                      title="Bahçıvanlar GES"
                      subtitle="248,52 kWp / 240 kWe"
                      tags={['İzleme', 'Yıllık Bakım', 'Termal Kontrol']}
                      fee="$ 1.584"
                    />
                    <LineCard
                      title="Ek Servis Paketi"
                      subtitle="Raporlama + aylık performans analizi"
                      tags={['Aylık Rapor', 'Yönetici Özeti']}
                      fee="$ 320"
                    />
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4">
                  <div className="text-sm font-black text-slate-900">Notlar ve Özel Maddeler</div>
                  <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-600">
                    Yenileme teklifinde saha ziyaret sıklığı aynı kalsın. 2026 ikinci yarı için inverter temizlik ve termal kamera hizmeti ayrı kalem olarak korunacak. Kur sabitleme maddesi satış ekibiyle tekrar gözden geçirilecek.
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-[24px] border border-slate-200 bg-slate-950 p-5 text-white">
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Sağ Özet Paneli</div>
                  <h3 className="mt-2 text-lg font-black tracking-tight">Kaydetmeden Önce</h3>

                  <div className="mt-5 space-y-3">
                    <SummaryRow label="Müşteri" value="Bahçıvanlar Basım" />
                    <SummaryRow label="Kontrat Tipi" value="İzleme + Bakım" />
                    <SummaryRow label="Santral Sayısı" value="2 kalem" />
                    <SummaryRow label="Toplam Bedel" value="$ 1.904" />
                    <SummaryRow label="Başlangıç" value="01.06.2026" />
                    <SummaryRow label="Tahmini Bitiş" value="01.06.2027" />
                  </div>

                  <div className="mt-5 rounded-2xl bg-white/10 px-4 py-3 text-sm text-slate-200">
                    Bu blok gerçek uygulamada sağ sabit panel gibi davranabilir. Kullanıcı formu doldururken toplamlar ve kritik eksikler burada anlık güncellenir.
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-slate-700" />
                    <h3 className="text-base font-black tracking-tight text-slate-900">Üretilecek Çıktılar</h3>
                  </div>
                  <div className="mt-4 space-y-3">
                    <OutputRow title="Sözleşme PDF" subtitle="İmzaya hazır versiyon" />
                    <OutputRow title="Proforma" subtitle="Müşteri paylaşımı için ticari özet" />
                    <OutputRow title="İç Onay Akışı" subtitle="Operasyon ve satış kontrol listesi" />
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-5">
                  <div className="text-base font-black tracking-tight text-slate-900">Aksiyon Alanı</div>
                  <div className="mt-4 grid gap-3">
                    <button className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800">
                      Taslağı Kaydet
                    </button>
                    <button className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                      Proforma Önizleme Oluştur
                    </button>
                    <button className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-500 transition hover:border-slate-300 hover:text-slate-700">
                      Vazgeç / Taslağı Sil
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <aside className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-slate-700" />
              <h2 className="text-base font-black tracking-tight text-slate-900">Bakım Ajandası</h2>
            </div>
            <div className="mt-4 space-y-3">
              {visitData.map((visit) => (
                <div key={visit.id} className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-slate-900">{visit.plant}</div>
                      <div className="text-xs text-slate-500">{visit.customer}</div>
                    </div>
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${visitTone[visit.status]}`}>
                      {visit.status}
                    </span>
                  </div>
                  <div className="mt-3 text-sm text-slate-700">{visit.focus}</div>
                  <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                    <span>{visit.team}</span>
                    <span>{visit.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <aside className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-slate-700" />
              <h2 className="text-base font-black tracking-tight text-slate-900">Doküman Akışı</h2>
            </div>
            <div className="mt-4 space-y-3">
              {documentData.map((doc) => (
                <div key={doc.id} className="rounded-[20px] border border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-slate-900">{doc.title}</div>
                      <div className="mt-1 text-xs text-slate-500">{doc.customer}</div>
                    </div>
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${docTone[doc.status]}`}>
                      {doc.status}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500">
                    <span>{doc.kind}</span>
                    <span>{doc.updatedAt}</span>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <aside className="rounded-[24px] border border-slate-200 bg-[radial-gradient(circle_at_top,#eff6ff,white_62%)] p-5 shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="flex items-center gap-2">
              <HardHat className="h-5 w-5 text-blue-700" />
              <h2 className="text-base font-black tracking-tight text-slate-900">Bu Versiyonda Denenenler</h2>
            </div>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
              <li>Bakım ajandası, sözleşme riski ve doküman üretimi aynı ekranda birlikte okunuyor.</li>
              <li>Grid yoğunluğu yerine karar odaklı kartlar kullanıldı; sonraki iterasyonda tablo da eklenebilir.</li>
              <li>Placeholder veriyle çalışıyor, mevcut bakım modülüne veri ya da davranış bağlamıyor.</li>
            </ul>
          </aside>
        </div>
      </section>
    </div>
  );
}

function MetricCard({
  icon,
  tone,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  tone: string;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
      <div className={`inline-flex rounded-2xl border p-3 ${tone}`}>{icon}</div>
      <div className="mt-4 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="mt-1 text-3xl font-black tracking-tight text-slate-900">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{hint}</div>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-700">{value}</div>
    </div>
  );
}

function FormField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="mt-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700">
        {value}
      </div>
    </div>
  );
}

function LineCard({
  title,
  subtitle,
  tags,
  fee,
}: {
  title: string;
  subtitle: string;
  tags: string[];
  fee: string;
}) {
  return (
    <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-sm font-black text-slate-900">{title}</div>
          <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600">
                {tag}
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-white px-3 py-2 text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Bedel</div>
          <div className="mt-1 text-lg font-black text-slate-900">{fee}</div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-2 text-sm">
      <span className="text-slate-300">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
  );
}

function OutputRow({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="text-sm font-bold text-slate-900">{title}</div>
      <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
    </div>
  );
}
